import { useQuery, useQueries, type UseQueryResult } from '@tanstack/react-query'
import { type PaginatedResponse, type DetailResponse } from '../../../api/client'
import { type PaginationOptions } from '../../../api/pagination'
import {
  fetchContact,
  fetchContacts,
  fetchRichContacts,
  fetchAllContacts,
  fetchAllRichContacts,
  searchContacts,
  type ContactFilters,
} from '../api/contactApi'
import { type Contact, type RichContact } from '../../../utils/types'
import { type PaginatedQueryParameters, type DetailQueryParameters } from '../../../api/hooks/utils/types'
import { useSmartListQuery, useSmartDetailQuery } from '../../../api/hooks/useSmartQueries'
import { contactKeys, richContactKeys } from '../utils/queryKeys'

/**
 * Hook that provides the available Contact instances that are matching the
 * specified query parameters.
 * @param parameters - The parameters that define which instances to fetch from the backend.
 * @param instances - Array of instance ids or urls to fetch from the backend.
 * @param enabled - Disabling the query will prevent it from running instantly
 *  after the parameters or instances changed.
 */
export function useContacts(
  parameters: PaginatedQueryParameters<Contact, ContactFilters>,
  instances: number[] | string[] | undefined = undefined,
  enabled = true,
): UseQueryResult<PaginatedResponse<Contact>> {
  return useSmartListQuery<Contact, ContactFilters>(
    {
      queryKeyFactory: contactKeys,
      queryParameters: parameters,
      queryFn: (_, ids: number[] | undefined) => {
        // If IDs are defined, it means a search was performed (or instances were provided)
        if (ids !== undefined) {
          // If search returned no results, return empty page immediately
          if (ids.length === 0) {
            return Promise.resolve({
              results: [],
              count: 0,
              next: null,
              previous: null,
            } as PaginatedResponse<Contact>)
          }
          // If search returned results, fetch them by ID without re-applying filters
          // (filters were already applied by the search)
          return fetchContacts({ ...parameters, filters: undefined }, ids)
        }
        // No search performed (normal list fetch), use original parameters including filters
        return fetchContacts(parameters, ids)
      },
      elasticSearchFn: () => searchContacts(parameters)
    },
    enabled,
    instances,
  )
}

/**
 * Hook to fetch paginated rich contacts
 */
export function useRichContacts(
  options: PaginationOptions = {},
  enabled = true
): UseQueryResult<PaginatedResponse<RichContact>, Error> {
  return useQuery({
    queryKey: richContactKeys.list({
      page: options.page,
      pageSize: options.pageSize,
      filters: options.filters,
      expand: options.expand,
    }),
    queryFn: () => fetchRichContacts(options),
    enabled,
  })
}

/**
 * Hook to fetch a single contact by ID or URL
 */
export function useContact(
  idOrUrl: number | string,
  parameters: DetailQueryParameters<Contact>,
  enabled = true,
): UseQueryResult<DetailResponse<Contact>> {
  return useSmartDetailQuery(
    idOrUrl,
    {
      queryKeyFactory: contactKeys,
      queryParameters: parameters,
      queryFn: (_, id) => fetchContact(id, parameters.expand),
    },
    enabled
  )
}

/**
 * Hook to fetch all contacts.
 */
export function useAllContacts(
  options: Omit<PaginationOptions, 'page'> = {},
  enabled = true
): UseQueryResult<Contact[], Error> {
  return useQuery({
    queryKey: [...contactKeys.all, 'all', options] as const,
    queryFn: () => fetchAllContacts(options),
    enabled,
  })
}

/**
 * Hook to fetch all rich contacts.
 */
export function useAllRichContacts(
  options: Omit<PaginationOptions, 'page'> = {},
  enabled = true
): UseQueryResult<RichContact[], Error> {
  return useQuery({
    queryKey: [...richContactKeys.all, 'all', options] as const,
    queryFn: () => fetchAllRichContacts(options),
    enabled,
  })
}

/**
 * Hook to fetch multiple contacts from Elasticsearch search results
 * Loads full contact data from the database for each contact ID
 */
export function useContactsFromSearch(
  contactIds: string[],
  expand?: string[],
  enabled = true
) {
  const queries = useQueries({
    queries: contactIds.map((id) => ({
      queryKey: contactKeys.detail(id, { expand }),
      queryFn: () => fetchContact(id, expand),
      enabled: enabled && !!id,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    })),
  })

  // Extract contacts from query results
  const contacts = queries
    .filter((query) => query.isSuccess && query.data)
    .map((query) => query.data as Contact)

  // Check if any queries are still loading
  const isLoading = queries.some((query) => query.isLoading)

  // Check if all queries are done (either success or error)
  const isIdle = queries.every((query) => query.status === 'pending')
  const isDone = queries.every((query) => query.status !== 'pending')

  // Check for errors
  const hasErrors = queries.some((query) => query.isError)
  const errors = queries
    .filter((query) => query.isError)
    .map((query) => query.error)

  // Calculate progress
  const totalQueries = contactIds.length
  const completedQueries = queries.filter(
    (query) => query.status !== 'pending'
  ).length
  const progress = totalQueries > 0 ? (completedQueries / totalQueries) * 100 : 0

  return {
    contacts,
    isLoading,
    isIdle,
    isDone,
    hasErrors,
    errors,
    progress,
    queries,
  }
}
